import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import FeatherIcon from "feather-icons-react";
import axios from "axios";
import { toast } from "react-toastify";
import { getUserId } from "../../shared/authentication/authentication";
import { toggleLoader } from "../../shared/actions/setting";

const CORPORATE_ID = "60a7794ef812231385f6dce0";

const emptyMaterialRow = () => ({ text: "", material: null, qty: "", length: "" });

const initialState = {
    departmentId: "",
    machineId: "",
    customerName: "",
    jobName: "",
    numberOfColours: "",
    materials: [emptyMaterialRow()],
    jobLength: "",
};

const materialLabel = (material) => {
    if (material.vns) {
        return material.vns;
    }
    return material.vnsNo;
};

const TextInput = ({ type, label, name, onChange, value, isRequired, mainStyle, error }) => {
    return <Form.Group className={`mb-3 ${mainStyle || ""}`}>
        <Form.Label className="text-secondary">{label}</Form.Label>
        <Form.Control type={type} name={name} value={value} onChange={onChange} className={`bg-light ${error ? "warning-input" : ""}`} required={isRequired} />
        {error && <p className="warning-input-msg">{error}</p>}
    </Form.Group>
}

const DropDown = ({ title, name, data, value, onChange, displayKey, isRequired, mainStyle, error }) => {
    return <Form.Group className={`mb-3 ${mainStyle || ""}`}>
        <Form.Label className="text-secondary">{title}</Form.Label>
        <select className={`form-control bg-light ${error ? "warning-input" : ""}`} name={name} value={value || ""} onChange={onChange} required={isRequired}>
            <option value="">{title}</option>
            {data && data.map((item, index) => (
                <option key={index} value={item.id}>{item[displayKey]}</option>
            ))}
        </select>
        {error && <p className="warning-input-msg">{error}</p>}
    </Form.Group>
}

const AddButton = ({ name, onClick }) => {
    return <div className="d-flex w-full flex-row-reverse mb-3">
        <button type="button" className="manualEntryBtn" onClick={onClick}>
            <span>{name}</span>
        </button>
    </div>
}

const MaterialSearchSelect = ({ label, value, onChange, onSelect, departmentId, machineId, error }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [results, setResults] = useState([]);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            axios.get(process.env.REACT_APP_HOST + 'user/' + getUserId() + '/materials', {
                params: {
                    query: value || undefined,
                    departmentId: departmentId || undefined,
                    machineId: machineId || undefined,
                },
            }).then(res => {
                setResults(res.data.content || []);
            }).catch(error => {
                console.error(error.response?.data?.message);
            });
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [value, departmentId, machineId]);

    const query = (value || "").trim().toLowerCase();
    const materialMatchesQuery = (material) => (
        (material.structure || "").toLowerCase() === query
        || (material.name || "").toLowerCase() === query
        || (material.vnsNo || "").toLowerCase() === query
    );
    const hasExactMatch = results.some(materialMatchesQuery);

    const items = [
        ...results.map(item => ({ type: "option", material: item })),
        ...(query && !hasExactMatch ? [{ type: "add", value }] : []),
    ];

    const selectOption = (material) => {
        onSelect(material);
        setIsOpen(false);
    };

    const confirmNew = () => {
        if (!value) {
            return;
        }
        onSelect(null);
        setIsOpen(false);
    };

    const chooseItem = (item) => {
        if (item.type === "option") {
            selectOption(item.material);
        } else {
            confirmNew();
        }
    };

    const onKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(prev => Math.min(prev + 1, items.length - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(prev => Math.max(prev - 1, 0));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (activeIndex >= 0 && items[activeIndex]) {
                chooseItem(items[activeIndex]);
            } else if (hasExactMatch) {
                selectOption(results.find(materialMatchesQuery));
            } else if (value) {
                confirmNew();
            }
        } else if (event.key === "Escape") {
            setIsOpen(false);
        }
    };

    return <Form.Group className="mb-3">
        {label && <Form.Label className="text-secondary">{label}</Form.Label>}
        <div className="searchCombo" ref={containerRef}>
            <div className="searchCombo-inputWrap">
                <FeatherIcon icon="search" />
                <Form.Control
                    type="text"
                    value={value || ""}
                    className={`bg-light ${error ? "warning-input" : ""}`}
                    placeholder="Search existing material or enter a new one"
                    autoComplete="off"
                    onChange={(event) => { onChange(event.target.value); setIsOpen(true); setActiveIndex(-1); }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={onKeyDown}
                    required
                />
            </div>
            {isOpen && items.length > 0 && <div className="searchCombo-list">
                {items.map((item, index) => (
                    item.type === "option"
                        ? <div
                            key={index}
                            className={`searchCombo-option ${index === activeIndex ? "is-active" : ""}`}
                            onClick={() => selectOption(item.material)}
                        >
                            {materialLabel(item.material)}
                        </div>
                        : <div
                            key={index}
                            className={`searchCombo-option searchCombo-option-add ${index === activeIndex ? "is-active" : ""}`}
                            onClick={confirmNew}
                        >
                            <FeatherIcon icon="plus-circle" />
                            <span>Add "{item.value}" as new material</span>
                        </div>
                ))}
            </div>}
        </div>
        {error && <p className="warning-input-msg">{error}</p>}
    </Form.Group>
}

const isBlank = (value) => !String(value ?? "").trim();

const JobDetailsEntry = () => {
    const dispatch = useDispatch();
    const [data, setData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [departmentList, setDepartmentList] = useState([]);
    const [machineList, setMachineList] = useState([]);
    const [filteredMachines, setFilteredMachines] = useState([]);

    useEffect(() => {
        dispatch(toggleLoader(true));
        axios.get(process.env.REACT_APP_HOST + 'user/' + getUserId() + '/corporate/' + CORPORATE_ID + '/department')
            .then(res => {
                setDepartmentList(res.data.content);
            }).catch(error => {
                console.error(error.response?.data?.message);
            }).finally(() => {
                dispatch(toggleLoader(false));
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        axios.get(process.env.REACT_APP_HOST + 'user/' + getUserId() + '/machine')
            .then(res => {
                setMachineList(res.data.content);
                setFilteredMachines(res.data.content);
            }).catch(error => {
                console.error(error.response?.data?.message);
            });
    }, []);

    const clearError = (field) => {
        setErrors(prev => {
            if (!prev[field]) {
                return prev;
            }
            const updated = { ...prev };
            delete updated[field];
            return updated;
        });
    }

    const clearMaterialError = (index, field) => {
        setErrors(prev => {
            if (!prev.materials || !prev.materials[index] || !prev.materials[index][field]) {
                return prev;
            }
            const materials = [...prev.materials];
            materials[index] = { ...materials[index] };
            delete materials[index][field];
            return { ...prev, materials };
        });
    }

    const handleChange = (event) => {
        setData(prev => ({ ...prev, [event.target.name]: event.target.value }));
        clearError(event.target.name);
    }

    const onDepartmentChange = (event) => {
        const departmentId = event.target.value;
        setData(prev => ({ ...prev, departmentId, machineId: "" }));
        setFilteredMachines(departmentId ? machineList.filter(item => item.departmentId === departmentId) : machineList);
        clearError("departmentId");
    }

    const onMachineChange = (event) => {
        setData(prev => ({ ...prev, machineId: event.target.value }));
        clearError("machineId");
    }

    const handleMaterialTextChange = (index, text) => {
        setData(prev => {
            const materials = [...prev.materials];
            materials[index] = { ...materials[index], text, material: null };
            return { ...prev, materials };
        });
        clearMaterialError(index, "text");
    }

    const handleMaterialSelect = (index, material) => {
        setData(prev => {
            const materials = [...prev.materials];
            materials[index] = {
                ...materials[index],
                text: material ? materialLabel(material) : materials[index].text,
                material,
                qty: material ? (material.qty ?? "") : materials[index].qty,
                length: material ? (material.length ?? "") : materials[index].length,
            };
            return { ...prev, materials };
        });
        clearMaterialError(index, "text");
    }

    const handleMaterialFieldChange = (index, field, value) => {
        setData(prev => {
            const materials = [...prev.materials];
            materials[index] = { ...materials[index], [field]: value };
            return { ...prev, materials };
        });
        clearMaterialError(index, field);
    }

    const addMaterialRow = () => {
        setData(prev => ({ ...prev, materials: [...prev.materials, emptyMaterialRow()] }));
    }

    const validate = () => {
        const newErrors = {};
        if (isBlank(data.departmentId)) newErrors.departmentId = "Department is required";
        if (isBlank(data.machineId)) newErrors.machineId = "Machine is required";
        if (isBlank(data.jobName)) newErrors.jobName = "Job Name is required";
        if (isBlank(data.customerName)) newErrors.customerName = "Customer Name is required";
        if (isBlank(data.numberOfColours)) newErrors.numberOfColours = "Number of Colors is required";
        if (isBlank(data.jobLength)) newErrors.jobLength = "Job Length is required";

        const materialErrors = data.materials.map(row => {
            const rowErrors = {};
            if (!row.material && isBlank(row.text)) rowErrors.text = "Material is required";
            if (isBlank(row.qty)) rowErrors.qty = "Quantity is required";
            if (isBlank(row.length)) rowErrors.length = "Length is required";
            return rowErrors;
        });
        if (materialErrors.some(rowErrors => Object.keys(rowErrors).length > 0)) {
            newErrors.materials = materialErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const onSubmit = (event) => {
        event.preventDefault();

        if (!validate()) {
            return;
        }

        const length = Number(data.jobLength) || 0;
        const materialRows = data.materials.filter(row => row.text.trim() || row.material);

        const payload = {
            jId: data.jobName.trim(),
            machineId: data.machineId,
            designName: "",
            customer: { name: data.customerName.trim() },
            rated: {
                output: length,
                speed: "180"
            },
            noOfColors: Number(data.numberOfColours) || 0,
            length: 0,
            inputMaterials: materialRows.map(row => {
                const quantity = Number(row.qty) || 0;
                const materialLength = Number(row.length) || 0;

                return row.material ? {
                    structure: row.material.structure,
                    vnsNo: row.material.vnsNo,
                    vnsCode: row.material.vnsCode,
                    name: row.material.name,
                    qty: quantity,
                    actualAmount: quantity,
                    length: materialLength,
                } : {
                    structure: "",
                    vnsNo: row.text.trim(),
                    qty: quantity,
                    actualAmount: quantity,
                    length: materialLength,
                };
            }),
        };

        dispatch(toggleLoader(true));

        axios.post(process.env.REACT_APP_HOST + 'user/' + getUserId() + '/job', payload)
            .then(() => {
                toast.success("Job created successfully", { autoClose: 3000 });
                setData(initialState);
                setErrors({});
                setFilteredMachines(machineList);
            })
            .catch(error => {
                console.error(error.response?.data?.message);
                toast.error(error.response?.data?.message || "Failed to create job");
            })
            .finally(() => {
                dispatch(toggleLoader(false));
            });
    }

    return <div id="listView" className="list-view">
        <Container className="m-t-8">
            <Form onSubmit={onSubmit} noValidate>
                <Row>
                    <Col>
                        <Card>
                            <label className="font-weight-bold pt-4 pl-3 font-size-18">
                                Job Details
                            </label>
                            <Card.Body>
                                <div className="d-flex">
                                    <DropDown
                                        mainStyle="w-50 mr-1"
                                        title="Department"
                                        name="departmentId"
                                        displayKey="name"
                                        data={departmentList}
                                        value={data.departmentId}
                                        onChange={onDepartmentChange}
                                        isRequired={true}
                                        error={errors.departmentId}
                                    />
                                    <DropDown
                                        mainStyle="w-50 ml-1"
                                        title="Machine"
                                        name="machineId"
                                        displayKey="name"
                                        data={filteredMachines}
                                        value={data.machineId}
                                        onChange={onMachineChange}
                                        isRequired={true}
                                        error={errors.machineId}
                                    />
                                </div>
                                <div className="d-flex">
                                    <TextInput
                                        mainStyle="w-50 mr-1"
                                        label="Job Name"
                                        type="text"
                                        name="jobName"
                                        value={data.jobName}
                                        onChange={handleChange}
                                        isRequired={true}
                                        error={errors.jobName}
                                    />
                                    <TextInput
                                        mainStyle="w-50 ml-1"
                                        label="Customer Name"
                                        type="text"
                                        name="customerName"
                                        value={data.customerName}
                                        onChange={handleChange}
                                        isRequired={true}
                                        error={errors.customerName}
                                    />
                                </div>
                                <div className="d-flex">
                                    <TextInput
                                        mainStyle="w-50 mr-1"
                                        label="Number of Colors"
                                        type="number"
                                        name="numberOfColours"
                                        value={data.numberOfColours}
                                        onChange={handleChange}
                                        isRequired={true}
                                        error={errors.numberOfColours}
                                    />
                                    <TextInput
                                        mainStyle="w-50 ml-1"
                                        label="Job Length (m)"
                                        type="number"
                                        name="jobLength"
                                        value={data.jobLength}
                                        onChange={handleChange}
                                        isRequired={true}
                                        error={errors.jobLength}
                                    />
                                </div>
                                {data.materials.map((row, index) => (
                                    <div key={index} className="manualEntryMargin">
                                        <MaterialSearchSelect
                                            label={index === 0 ? "Material Number" : undefined}
                                            value={row.text}
                                            onChange={(text) => handleMaterialTextChange(index, text)}
                                            onSelect={(material) => handleMaterialSelect(index, material)}
                                            departmentId={data.departmentId}
                                            machineId={data.machineId}
                                            error={errors.materials?.[index]?.text}
                                        />
                                        {(row.material || row.text.trim()) && <div className="d-flex">
                                            <TextInput
                                                mainStyle="w-50 mr-1"
                                                label="Quantity (kg)"
                                                type="number"
                                                value={row.qty}
                                                onChange={(event) => handleMaterialFieldChange(index, "qty", event.target.value)}
                                                isRequired={true}
                                                error={errors.materials?.[index]?.qty}
                                            />
                                            <TextInput
                                                mainStyle="w-50 ml-1"
                                                label="Length (m)"
                                                type="number"
                                                value={row.length}
                                                onChange={(event) => handleMaterialFieldChange(index, "length", event.target.value)}
                                                isRequired={true}
                                                error={errors.materials?.[index]?.length}
                                            />
                                        </div>}
                                    </div>
                                ))}
                                <AddButton name="ADD MATERIAL" onClick={addMaterialRow} />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <div className="w-full pt-3 pb-3 d-flex flex-row-reverse">
                    <button type="submit" className="manualEntryAddBtn">
                        <span>Save</span>
                    </button>
                </div>
            </Form>
        </Container>
    </div>
}

export default JobDetailsEntry;
